<template>
  <div class="user-stats-panel">
    <h2>Your Stats</h2>
    
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">📝</div>
        <div class="stat-content">
          <div class="stat-label">Total Reviews</div>
          <div class="stat-value">{{ stats.reviewCount }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">📸</div>
        <div class="stat-content">
          <div class="stat-label">Photo Reviews</div>
          <div class="stat-value">{{ stats.photoReviewCount }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">🗺️</div>
        <div class="stat-content">
          <div class="stat-label">States Visited</div>
          <div class="stat-value">{{ stats.statesVisited?.size || 0 }}/50</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">👥</div>
        <div class="stat-content">
          <div class="stat-label">Followers</div>
          <div class="stat-value">{{ stats.followerCount || 0 }}</div>
        </div>
      </div>
    </div>

    <div v-if="earnedBadges.length > 0" class="badges-section">
      <h3 class="badges-title">🏆 Your Achievements</h3>
      <div class="badges-grid-display">
        <div v-for="badgeId in earnedBadges" :key="badgeId" class="badge-card" :title="getBadgeDescription(badgeId)">
          <div v-if="getBadgeImage(badgeId)" class="state-badge-circle-large">
            <img :src="getBadgeImage(badgeId)" class="badge-img-large" :alt="getBadgeName(badgeId)" />
          </div>
          <div v-else class="badge-emoji">{{ getBadgeEmoji(badgeId) }}</div>
          <div class="badge-name">{{ getBadgeName(badgeId) }}</div>
        </div>
      </div>
    </div>

    <div class="achievement-progress">
      <h3>Next Achievement</h3>
      <div v-for="badge in nextBadges" :key="badge.id" class="progress-item">
        <div class="progress-header">
          <span class="badge-preview">{{ badge.emoji }}</span>
          <span class="badge-name">{{ badge.name }}</span>
          <span class="progress-count">{{ getProgress(badge.id) }}%</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: getProgress(badge.id) + '%' }"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { calculateEarnedBadges, BADGES, getBadgeById } from '../utils/badges';
import { showToast } from '../utils/toast.js';
import BadgeDisplay from './BadgeDisplay.vue';

export default {
  name: 'UserStatsPanel',
  components: { BadgeDisplay },
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const stats = ref({
      reviewCount: 0,
      photoReviewCount: 0,
      statesVisited: new Set(),
      followerCount: 0
    });
    const earnedBadges = ref([]);
    const loading = ref(true);

    const loadUserStats = async () => {
      loading.value = true;
      try {
        // Get user's reviews
        const reviewsSnap = await getDocs(
          query(collection(db, 'reviews'), where('userId', '==', props.user.uid))
        );

        const reviews = reviewsSnap.docs.map(doc => doc.data());
        
        // Count photo reviews and collect states from restaurants
        let photoReviewCount = 0;
        const statesVisited = new Set();
        const reviewsByState = {};
        const userReviewsByState = {}; // Track earliest review per state
        
        for (const review of reviews) {
          if (review.photoURL) {
            photoReviewCount++;
          }
          
          // Fetch the restaurant document to get the state
          if (review.restaurantId) {
            try {
              const restaurantRef = doc(db, 'restaurants', review.restaurantId);
              const restaurantSnap = await getDoc(restaurantRef);
              if (restaurantSnap.exists()) {
                const restaurantData = restaurantSnap.data();
                if (restaurantData.state) {
                  const state = restaurantData.state.toUpperCase();
                  statesVisited.add(state);
                  reviewsByState[state] = (reviewsByState[state] || 0) + 1;

                  // Track earliest review for this state
                  const reviewTime = review.createdAt?.toMillis?.() || (review.createdAt ? new Date(review.createdAt).getTime() : 0);
                  if (!userReviewsByState[state] || reviewTime < userReviewsByState[state].timestamp) {
                    userReviewsByState[state] = { ...review, timestamp: reviewTime };
                  }
                }
              }
            } catch (error) {
              console.error('Error fetching restaurant:', error);
            }
          }
        }

        // Get follower count
        const followersSnap = await getDocs(
          query(collection(db, 'follows'), where('followingId', '==', props.user.uid))
        );

        // Get all reviews to check for first-to-review badges
        const allReviewsSnap = await getDocs(collection(db, 'reviews'));
        const allReviews = allReviewsSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }));

        // Count first-to-review achievements
        let firstReviewCount = 0;
        const restaurantReviewMap = {};
        allReviews.forEach(review => {
          if (!restaurantReviewMap[review.restaurantId]) {
            restaurantReviewMap[review.restaurantId] = [];
          }
          restaurantReviewMap[review.restaurantId].push(review);
        });

        reviews.forEach(userReview => {
          const restaurantReviews = restaurantReviewMap[userReview.restaurantId] || [];
          if (restaurantReviews.length > 0) {
            // Sort by timestamp to find the first review
            const sorted = restaurantReviews.sort((a, b) => {
              const timeA = a.createdAt?.toMillis?.() || (a.createdAt ? new Date(a.createdAt).getTime() : 0);
              const timeB = b.createdAt?.toMillis?.() || (b.createdAt ? new Date(b.createdAt).getTime() : 0);
              return timeA - timeB;
            });
            if (sorted[0].userId === props.user.uid) {
              firstReviewCount++;
            }
          }
        });

        // Check for "First in State" achievements
        const stateBadges = [];
        const getTime = (dateObj) => dateObj?.toMillis?.() || (dateObj ? new Date(dateObj).getTime() : 0);

        for (const state of statesVisited) {
          const userEarliest = userReviewsByState[state];
          if (!userEarliest) continue;

          // Get all restaurants in this state to check global history
          const stateRestaurantsSnap = await getDocs(query(collection(db, 'restaurants'), where('state', '==', state)));
          const stateRestaurantIds = new Set(stateRestaurantsSnap.docs.map(d => d.id));

          // Check if any review in allReviews is for these restaurants AND is earlier than userEarliest
          const isFirst = !allReviews.some(r => {
            if (!stateRestaurantIds.has(r.restaurantId)) return false;
            if (r.id === userEarliest.id) return false; // Same review
            const rTime = getTime(r.createdAt);
            return rTime < userEarliest.timestamp;
          });

          if (isFirst) {
            stateBadges.push(`first-in-${state}`);
          }
        }

        const newStats = {
          reviewCount: reviews.length,
          photoReviewCount,
          statesVisited,
          reviewsByState,
          firstReviews: firstReviewCount,
          followerCount: followersSnap.docs.length,
          uid: props.user.uid
        };

        // Calculate newly earned badges and show notifications
        const previousBadges = new Set(earnedBadges.value);
        const calculatedBadges = calculateEarnedBadges(newStats);
        const newBadges = [...calculatedBadges, ...stateBadges];
        
        // Show toast notifications for newly earned badges
        newBadges.forEach(badgeId => {
          if (!previousBadges.has(badgeId)) {
            if (badgeId.startsWith('first-in-')) {
              const state = badgeId.replace('first-in-', '');
              showToast(`🎉 Congratulations! You are the first to review in ${state}! 🗺️`, 'success', 4000);
            } else {
              const badge = BADGES[Object.keys(BADGES).find(key => BADGES[key].id === badgeId)];
              if (badge) {
              showToast(`🎉 Congratulations! You earned the ${badge.name} badge! ${badge.emoji}`, 'success', 3000);
              }
            }
          }
        });

        stats.value = newStats;
        earnedBadges.value = newBadges;

        // Persist badges to user profile and propagate to reviews/comments
        try {
          const userRef = doc(db, 'users', props.user.uid);
          const userSnap = await getDoc(userRef);
          const currentBadges = userSnap.exists() ? (userSnap.data().badges || []) : [];

          const sortedNewBadges = [...newBadges].sort();
          const sortedCurrentBadges = [...currentBadges].sort();
          const badgesChanged = JSON.stringify(sortedNewBadges) !== JSON.stringify(sortedCurrentBadges);

          // Check if ANY review needs updating (check all reviews to ensure sync)
          let reviewsNeedUpdate = false;
          const sortedNewBadgesJson = JSON.stringify(sortedNewBadges);
          if (reviews.length > 0) {
            reviewsNeedUpdate = reviews.some(review => {
              const currentReviewBadges = review.userBadges || [];
              return JSON.stringify([...currentReviewBadges].sort()) !== sortedNewBadgesJson;
            });
          }

          if (badgesChanged) {
            // 1. Update User Profile
            await updateDoc(userRef, {
              badges: newBadges
            });
          }

          if (badgesChanged || reviewsNeedUpdate) {
            console.log('Propagating badges to reviews and comments...');
            // 2. Update all user's reviews (so they show up on Home feed)
            const reviewUpdates = reviewsSnap.docs.map(reviewDoc => 
              updateDoc(reviewDoc.ref, { userBadges: newBadges })
            );
            
            // 3. Update all user's comments
            const commentsSnap = await getDocs(query(collection(db, 'comments'), where('userId', '==', props.user.uid)));
            const commentUpdates = commentsSnap.docs.map(commentDoc => 
              updateDoc(commentDoc.ref, { userBadges: newBadges })
            );

            await Promise.all([...reviewUpdates, ...commentUpdates]);
            console.log('Synced badges to profile, reviews, and comments');
          }
        } catch (error) {
          console.error('Error updating user badges:', error);
        }

      } catch (error) {
        console.error('Error loading user stats:', error);
      } finally {
        loading.value = false;
      }
    };

    const nextBadges = computed(() => {
      return Object.values(BADGES)
        .filter(badge => !earnedBadges.value.includes(badge.id))
        .slice(0, 3);
    });

    const getProgress = (badgeId) => {
      const badge = Object.values(BADGES).find(b => b.id === badgeId);
      if (!badge) return 0;

      let progress = 0;

      if (badgeId.includes('reviewer-')) {
        const requiredCount = parseInt(badgeId.match(/\d+/)[0]);
        progress = Math.min(100, (stats.value.reviewCount / requiredCount) * 100);
      } else if (badgeId === 'photo-master') {
        progress = Math.min(100, (stats.value.photoReviewCount / 10) * 100);
      } else if (badgeId === 'all-50-states') {
        progress = Math.min(100, ((stats.value.statesVisited?.size || 0) / 50) * 100);
      }

      return Math.round(progress);
    };

    const getBadgeName = (badgeId) => {
      if (badgeId.startsWith('first-in-')) {
        const state = badgeId.replace('first-in-', '');
        return `First in ${state}`;
      }
      const badge = getBadgeById(badgeId);
      return badge ? badge.name : '';
    };

    const getBadgeImage = (badgeId) => {
      if (badgeId.startsWith('first-in-')) {
        const state = badgeId.replace('first-in-', '').toLowerCase();
        return `https://raw.githubusercontent.com/djaiss/mapsicon/master/us/${state}/128.png`;
      }
      return null;
    };

    const getBadgeEmoji = (badgeId) => {
      const badge = getBadgeById(badgeId);
      return badge ? badge.emoji : '';
    };

    const getBadgeDescription = (badgeId) => {
      if (badgeId.startsWith('first-in-')) {
        const state = badgeId.replace('first-in-', '');
        return `First person to review a restaurant in ${state}`;
      }
      const badge = getBadgeById(badgeId);
      return badge ? badge.description : '';
    };

    onMounted(() => {
      loadUserStats();
    });

    return {
      stats,
      earnedBadges,
      nextBadges,
      getProgress,
      loading,
      getBadgeName,
      getBadgeImage,
      getBadgeEmoji,
      getBadgeDescription
    };
  }
};
</script>

<style scoped>
.user-stats-panel {
  background: var(--card-background);
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 8px var(--shadow-color);
}

.user-stats-panel h2 {
  color: var(--primary-color);
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border: 1px solid var(--primary-color);
  border-radius: 8px;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--shadow-color);
}

.stat-icon {
  font-size: 1.5rem;
}

.stat-content {
  flex: 1;
  width: 100%;
}

.stat-label {
  font-size: 0.65rem;
  color: var(--text-color);
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.2;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-top: 0.1rem;
}

.badges-section {
  margin-bottom: 1.5rem;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
  padding: 1.25rem;
}

.badges-title {
  margin-top: 0;
  margin-bottom: 1rem;
  color: #d48806;
  font-size: 1.1rem;
}

.badges-grid-display {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.badge-card {
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  min-width: 90px;
}

.badge-emoji {
  font-size: 2rem;
  margin-bottom: 0.25rem;
}

.state-badge-circle-large {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;
  background-color: #e3f2fd;
  border: 2px solid #90caf9;
  border-radius: 50%;
  margin-bottom: 0.25rem;
}

.badge-img-large {
  width: 2.2rem;
  height: 2.2rem;
  object-fit: contain;
  margin-bottom: 0;
}

.badge-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--secondary-color);
  text-align: center;
}

.achievement-progress {
  padding-top: 1.5rem;
  border-top: 2px solid var(--background-color);
}

.achievement-progress h3 {
  color: var(--secondary-color);
  font-size: 1rem;
  margin: 0 0 0.75rem 0;
}

.progress-item {
  margin-bottom: 1rem;
}

.progress-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
  font-size: 0.9rem;
}

.badge-preview {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.badge-name {
  flex: 1;
  color: var(--secondary-color);
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.progress-count {
  background: var(--primary-color);
  color: white;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.progress-bar {
  background: var(--background-color);
  border-radius: 4px;
  height: 6px;
  overflow: hidden;
}

.progress-fill {
  background: linear-gradient(90deg, var(--primary-color) 0%, #2980b9 100%);
  height: 100%;
  transition: width 0.3s ease;
}

@media (min-width: 769px) {
  .user-stats-panel {
    padding: 2rem;
  }

  .user-stats-panel h2 {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .stat-card {
    flex-direction: row;
    text-align: left;
    padding: 1rem;
    gap: 1rem;
  }

  .stat-icon {
    font-size: 2rem;
  }

  .stat-label {
    font-size: 0.8rem;
    letter-spacing: 0.5px;
  }

  .stat-value {
    font-size: 1.75rem;
    margin-top: 0.25rem;
  }

  .badges-section {
    margin-bottom: 2rem;
  }

  .achievement-progress {
    padding-top: 2rem;
  }

  .achievement-progress h3 {
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .progress-item {
    margin-bottom: 1.5rem;
  }

  .progress-header {
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }

  .badge-preview {
    font-size: 1.5rem;
  }

  .badge-name {
    white-space: normal;
  }

  .progress-count {
    padding: 0.25rem 0.5rem;
    font-size: 0.85rem;
  }
}
</style>
